/**
 * R-73 — publish-time section depth gate.
 *
 * Validates per-report-type minimum content depth and detects stub-template
 * repeats (3+ sections sharing identical prose) before initial publish.
 *
 * Enforcement: `runSectionDepthGate` stamps failures onto
 * `report.qaFindings.sectionDepth`; block-severity entries then trigger the
 * QA-1 quality hold (resolveQualityHold → qualityHold stamp → export barred),
 * matching the R-72 distribution and R-62/64 drift gates. Independently,
 * `VITE_PUBLISH_SECTION_DEPTH_GATE=block` hard-fails the publish (throw)
 * instead of holding — default is the hold path.
 */
import {
  DEFAULT_PROSE_MIN,
  SECTION_DEPTH_REQUIREMENTS,
} from '../config/sectionDepthRequirements';
import type { ReportTypeId } from '../config/reportTypes';
import { getSectionsForType } from '../config/reportSections';
import {
  fieldPathForSection,
  getReportFieldValue,
  hasSectionFieldContent,
} from './sectionFieldPaths';
import {
  isCustomBriefQuestionSectionId,
  parseCustomBriefQuestionSectionId,
} from './customBriefSections';
import type { SiteRagReport } from '../types/siteRag';
import { findingClaimEntriesToStrings, formatFindingClaimDisplay } from './findingClaim';
import { degradedRemedyForKind, type NarrativeFailureKind } from './classifyNarrativeFailure';
import { withoutPublicSentimentMetaV2 } from './sentiment/publicSentimentMetaV2Contract';

export interface SectionDepthFailure {
  sectionId: string;
  kind: 'below-min-chars' | 'below-min-bullets' | 'bullets-too-short' | 'stub-template-repeat';
  severity: 'block' | 'warn';
  observed: { chars?: number; bulletCount?: number; shortBullets?: number };
  threshold: { chars?: number; bullets?: number; bulletChars?: number };
  detail: string;
}

export interface SectionDepthResult {
  passed: boolean;
  failures: SectionDepthFailure[];
}

export type PublishSectionDepthGateMode = 'warn-only' | 'block';

/** Sections excluded from stub-template repeat detection (non-body chrome). */
const STUB_SKIP_SECTION_IDS = new Set([
  'cover',
  'toc',
  'blindSpots',
  'references',
  'end',
  'closing',
  'radarChart',
  'lcoeChart',
  'climateProfile',
  'scoreProvenance',
  'scoreDrivers',
  'dataQualityNotes',
]);

/** Mirrors `emptySectionBodies.ts` FIELD_MAP — read-only serialization paths. */
export const SECTION_FIELD_CANDIDATES: Record<string, readonly string[]> = {
  executiveSummary: ['executiveSummary', 'summary', 'customBriefExecutiveSummary'],
  projectIntentOverview: ['projectIntentOverview', 'summary'],
  btmOverview: ['btmOptions', 'behindTheMeterAnalysis'],
  gridStudy: ['gridStudy', 'infrastructureInterconnection'],
  // STAGE 5.1 — `'gridStudy'` REMOVED from the head of this chain.
  //
  // It was harmless while `gridCapacity` was power-availability-only: power
  // reports have no `gridStudy` section, so the alias never resolved. Now that
  // BTM Feasibility carries BOTH sections, the fallback actively bleeds one
  // section's prose into the other — `fieldPathForSection` resolves
  // `powerAnalysis.substationAssessment` first, and when the model legitimately
  // omits it (the prompt explicitly permits that), the chain used to hand back
  // `gridStudy`'s text instead of nothing.
  //
  // Two things went wrong with that: the depth gate reads a borrowed section as
  // POPULATED, masking a real omission, and R-73 reports the two sections as
  // duplicate prose on every such report. Both are the hollow-report failure
  // this stage exists to avoid, so the alias goes.
  gridCapacity: ['gridCapacity', 'powerAnalysis'],
  transportStudy: ['transportStudy'],
  connectivityStudy: ['connectivityStudy'],
  sitingNotes: ['sitingNotes'],
  climateSevereWeather: ['climateSevereWeather'],
  riskAssessment: ['risks', 'developmentRisks', 'connectionRisks'],
  actionPlan: ['developmentPathway', 'recommendations', 'actionPlan'],
  extendedData: ['datasets'],
  nepaScreening: ['nepaScreening', 'executiveSummary'],
  ecologicalAssessment: ['ecologicalAssessment'],
  waterResources: ['waterResources'],
  airQualityEmissions: ['airQualityEmissions'],
  floodNaturalHazards: ['floodNaturalHazards'],
  culturalHistorical: ['culturalHistorical'],
  landUseZoning: ['landUseZoning'],
  noiseVisualImpact: ['noiseVisualImpact'],
  mitigationPlan: ['mitigationPlan', 'recommendations'],
  permittingPathway: ['permittingPathway'],
  environmentalRiskMatrix: ['risks', 'developmentRisks'],
  alternativesAnalysis: ['alternativesAnalysis'],
  cumulativeImpacts: ['cumulativeImpacts'],
  publicParticipationPlan: ['publicParticipationPlan'],
  interconnectionQueue: ['interconnectionQueue'],
  curtailmentRisk: ['curtailmentRisk'],
  rateStructures: ['rateStructures'],
  btmTechComparison: ['btmOptions'],
  lcoeBreakdown: ['lcoeBreakdown', 'btmOptions'],
  gridVsBtm: ['gridVsBtm', 'btmOptions'],
  powerRiskMatrix: ['risks', 'developmentRisks'],
  supplyAvailability: ['supplyAvailability', 'waterResources'],
  groundwater: ['groundwater'],
  waterRights: ['waterRights'],
  droughtResilience: ['droughtResilience'],
  coolingByTech: ['coolingByTech', 'btmOptions'],
  wastewaterStormwater: ['wastewaterStormwater'],
  conservation: ['conservation'],
  totalWaterCost: ['totalWaterCost'],
  waterRiskMatrix: ['risks', 'developmentRisks'],
  fiberRings: ['fiberRings', 'connectivityStudy'],
  latencyModel: ['latencyModel'],
  carrierDiversity: ['carrierDiversity'],
  ixProximity: ['ixProximity'],
  submarineCable: ['submarineCable'],
  networkResilience: ['networkResilience'],
  bandwidthCapacity: ['bandwidthCapacity'],
  capexOpex: ['capexOpex'],
  connectivityRiskMatrix: ['risks', 'developmentRisks'],
  parcelOverview: ['parcelOverview'],
  topography: ['topography'],
  buildingPlacement: ['buildingPlacement'],
  setbackCompliance: ['setbackCompliance'],
  parkingAccess: ['parkingAccess'],
  stormwater: ['stormwater'],
  utilityRouting: ['utilityRouting'],
  phasedBuildout: ['phasedBuildout', 'developmentPathway'],
  maxCapacity: ['maxCapacity'],
  constructabilityAssessment: ['constructabilityAssessment'],
  costRangeEstimate: ['costRangeEstimate'],
  riskMatrix: ['risks', 'developmentRisks'],
  btmViability: ['btmViabilityAssessment'],
  crossReportSynthesis: ['crossReportSynthesis'],
  criticalRisks: ['executiveSummaryCriticalRisks'],
  sourceReports: ['executiveSummarySourcesUsed'],
  procurementOverview: ['supplyChainAnalysis', 'executiveSummary'],
  criticalPathProcurement: ['supplyChainAnalysis'],
  supplyChainRisks: ['supplyChainAnalysis'],
  supplierRiskRanking: ['supplyChainAnalysis'],
  quoteComparisons: ['supplyChainAnalysis'],
  supplyChainRiskMatrix: ['supplyChainAnalysis'],
  projectContext: ['customBriefProjectContext'],
  customBriefQuestions: ['customBriefAnswers'],
  executive: ['executive'],
  stakeholderMap: ['stakeholderMap'],
  perBtmSentiment: ['perBtmSentiment'],
  themeDeepDives: ['themeDeepDives'],
  trendAnalysis: ['trendAnalysis'],
  comparableSiteBenchmark: ['comparableSiteBenchmark'],
  mitigationPlaybook: ['mitigationPlaybook'],
  sentimentResearchDetail: ['publicSentimentMeta', 'datasets'],
  appendix: ['sentimentAppendix', 'appendix'],
};

export function readPublishSectionDepthGateMode(): PublishSectionDepthGateMode {
  const raw = readEnvFlag('VITE_PUBLISH_SECTION_DEPTH_GATE');
  if (raw === 'block') return 'block';
  return 'warn-only';
}

export function checkSectionDepth(
  sections: Array<{ id: string; content: string }>,
  reportType: ReportTypeId,
  omittedSections?: ReadonlySet<string>,
  /**
   * UNIFORM-STUB COLLAPSE attribution (2026-08-25, William): while the report
   * is STILL degraded, the collapsed stub finding demotes to ONE warn with
   * the kind-specific remedy instead of N blocks. `degradedMode` decides;
   * `narrativeFailureKind` only words the remedy (see the collapse below).
   */
  provider?: { degradedMode?: boolean; narrativeFailureKind?: NarrativeFailureKind } | null,
): SectionDepthResult {
  const requirements = SECTION_DEPTH_REQUIREMENTS[reportType] ?? [];
  const failures: SectionDepthFailure[] = [];
  const sectionById = new Map(sections.map((s) => [s.id, s]));
  /*
   * OMISSION-AWARE SEVERITY (2026-08-11) — a section with an explicit
   * omission record (its evidence domain provably has nothing; see
   * `reportGeneration/sectionOmissions`) cannot be fixed by regeneration, so
   * a BLOCK demanding depth is unpassable. It demotes to warn and says why.
   * Sections without a record keep blocking — an unexplained empty section is
   * still a generator defect this gate exists to catch.
   */
  const omissionSeverity = (sectionId: string, base: 'block' | 'warn'): 'block' | 'warn' =>
    base === 'block' && omittedSections?.has(sectionId) ? 'warn' : base;
  const omissionSuffix = (sectionId: string): string =>
    omittedSections?.has(sectionId)
      ? ' Explicitly omitted: evidence sources returned no facts — add sources; regeneration cannot fill it.'
      : '';

  for (const req of requirements) {
    const section = sectionById.get(req.sectionId);
    if (!section) {
      failures.push({
        sectionId: req.sectionId,
        kind: 'below-min-chars',
        severity: omissionSeverity(req.sectionId, req.severity ?? 'block'),
        observed: { chars: 0 },
        threshold: { chars: req.minChars ?? DEFAULT_PROSE_MIN },
        detail: `Section "${req.sectionId}" missing from report.${omissionSuffix(req.sectionId)}`,
      });
      continue;
    }
    if (req.optional) continue;

    const content = section.content.trim();
    if (req.minChars) {
      if (content.length < req.minChars) {
        failures.push({
          sectionId: req.sectionId,
          kind: 'below-min-chars',
          severity: omissionSeverity(req.sectionId, req.severity ?? 'block'),
          observed: { chars: content.length },
          threshold: { chars: req.minChars },
          detail: `Section "${req.sectionId}" has ${content.length} chars; minimum ${req.minChars}.${omissionSuffix(req.sectionId)}`,
        });
      }
    }
    if (req.minBullets) {
      const bullets = extractBullets(content);
      if (bullets.length < req.minBullets) {
        failures.push({
          sectionId: req.sectionId,
          kind: 'below-min-bullets',
          severity: req.severity ?? 'block',
          observed: { bulletCount: bullets.length },
          threshold: { bullets: req.minBullets },
          detail: `Section "${req.sectionId}" has ${bullets.length} bullets; minimum ${req.minBullets}.`,
        });
      } else if (req.minBulletChars) {
        const short = bullets.filter((b) => b.length < req.minBulletChars!).length;
        if (short > 0) {
          failures.push({
            sectionId: req.sectionId,
            kind: 'bullets-too-short',
            severity: req.severity ?? 'block',
            observed: { bulletCount: bullets.length, shortBullets: short },
            threshold: { bullets: req.minBullets, bulletChars: req.minBulletChars },
            detail: `${short} of ${bullets.length} bullets in "${req.sectionId}" are shorter than ${req.minBulletChars} chars.`,
          });
        }
      }
    }
  }

  /*
   * UNIFORM-STUB COLLAPSE (2026-08-25, William) — one upstream failure must
   * read as ONE finding, not N echoes. When the narrative provider fails
   * wholesale (live: the 2026-08-18 Vertex billing dunning), every section
   * receives the same short placeholder; the per-section depth loop then
   * minted N "has 17 chars; minimum M" blocks and the template-stub detector
   * missed them entirely (its 30-char floor exists to avoid false positives
   * on legitimate short mirrors, which a FAILING section is not).
   *
   * ONE finding per IDENTICAL-CONTENT GROUP — never a union across groups, so
   * the finding can truthfully name what every section it subsumes contains.
   * A group collapses when it is either
   *   (a) a template stub — >=30 chars repeated across >=3 distinct alias
   *       FAMILIES (designed mirrors of one canonical narrative collapse to a
   *       single family and pass), or
   *   (b) a uniform stub — >=3 of its sections independently FAIL a depth
   *       requirement, of ANY kind, so bullet-floor report types and report
   *       types carrying fewer than three prose minimums are covered too.
   *
   * Two exclusions, both there to keep the collapse from erasing truth:
   *   - EMPTY sections. Absence carries no signature: N empty sections are as
   *     easily N distinct causes (missing field, serializer gap, unevidenced
   *     domain) as one shared outage, and "" is not a placeholder anyone
   *     stamped.
   *   - Explicitly-OMITTED sections. Their failure is provably not a provider
   *     echo, it already demotes to warn, and its remedy ("add sources";
   *     unfixable by re-running) would be replaced by one that cannot work.
   */
  const groupable = sections.filter(
    (s) => !STUB_SKIP_SECTION_IDS.has(s.id) && !omittedSections?.has(s.id),
  );
  const failingIds = new Set(failures.map((f) => f.sectionId));
  const byContent = new Map<string, { ids: Set<string>; chars: number }>();
  for (const s of groupable) {
    const norm = normalizeStubProse(s.content);
    if (!norm) continue;
    const group = byContent.get(norm) ?? { ids: new Set<string>(), chars: s.content.trim().length };
    group.ids.add(s.id);
    byContent.set(norm, group);
  }
  const collapses: Array<{ ids: string[]; chars: number }> = [];
  for (const [norm, group] of byContent) {
    if (group.ids.size < 3) continue;
    const ids = [...group.ids].sort();
    const families = new Set(ids.map((id) => STUB_ALIAS_FAMILY[id] ?? id));
    const templateStub = norm.length >= 30 && families.size >= 3;
    const uniformStub = ids.filter((id) => failingIds.has(id)).length >= 3;
    if (templateStub || uniformStub) collapses.push({ ids, chars: group.chars });
  }

  if (collapses.length > 0) {
    /*
     * ATTRIBUTION reads the LIVE degraded flag, never the generation
     * telemetry. `generationTelemetry.narrativeFailureKind` is an immutable
     * record of what happened during GENERATION (the admin failure-rate
     * dashboard counts it) and SURVIVES a successful "Refresh narrative",
     * which clears only degradedMode/degradedReason — trusting it would
     * demote residual stubs on a refreshed, fully exportable report. The
     * demotion's justification is `degradedMode` itself: it is the flag
     * `evaluateReportDelivery` hard-bars export/publish on, so one warn costs
     * no protection exactly while it is set. The kind only supplies the
     * remedy wording.
     */
    const attributed = provider?.degradedMode === true;
    const remedy = attributed
      ? degradedRemedyForKind(provider?.narrativeFailureKind).action
      : 'Expand each named section with distinct, site-specific content; re-run the report if the repetition is a generation artifact.';
    const collapsedIds = new Set(collapses.flatMap((c) => c.ids));
    const kept = failures.filter((f) => !collapsedIds.has(f.sectionId));
    for (const group of collapses) {
      const subsumedFailures = failures.filter((f) => group.ids.includes(f.sectionId));
      // The per-section counts survive the collapse on the unattributed path:
      // this detail is what the P3b retry overlay hands the generator, and
      // "N sections are thin" is not an instruction it can act on.
      const shortfalls = group.ids
        .filter((id) => subsumedFailures.some((f) => f.sectionId === id))
        .map((id) => {
          const own = subsumedFailures.filter((f) => f.sectionId === id).map(describeShortfall);
          return `${id} (${own.join('; ')})`;
        });
      kept.push({
        sectionId: group.ids.join(SUBSUMED_SECTION_ID_SEPARATOR),
        kind: 'stub-template-repeat',
        severity: attributed ? 'warn' : 'block',
        observed: { chars: group.chars },
        threshold: {},
        detail: attributed
          ? `${group.ids.length} sections carry the same placeholder text — one generation-wide narrative failure, not ${group.ids.length} content defects. ${remedy} Subsumed sections: ${group.ids.join(', ')}.`
          // No recorded provider failure ⇒ no evidence of a generation-wide
          // outage. State the observation, not a diagnosis the report cannot
          // support, and keep every per-section shortfall.
          : `${group.ids.length} sections share identical prose: ${group.ids.join(', ')}.`
            + (shortfalls.length > 0 ? ` Depth shortfalls: ${shortfalls.join('; ')}.` : '')
            + ' No provider failure is recorded on this report, so the repetition is not'
            + ` attributable to one generation-wide failure. ${remedy}`,
      });
    }
    return { passed: kept.filter((f) => f.severity === 'block').length === 0, failures: kept };
  }

  return { passed: failures.filter((f) => f.severity === 'block').length === 0, failures };
}

/**
 * The ONE currency the two stub detectors share.
 *
 * This gate (R-73) and the auditor's Tier-2k both answer the same question —
 * "do several sections carry byte-identical prose?" — but they name sections
 * in INCOMPATIBLE namespaces: R-73 works in canonical section ids resolved
 * through SECTION_FIELD_CANDIDATES/`fieldPathForSection`, Tier-2k works in raw
 * report FIELD names, and each yields sections the other never sees. Their
 * CONTENT is the only comparable half, so the overlap test in `runUserAudit`
 * compares normalized prose — which is sound only while both sides normalize
 * through this function. Single owner: never re-inline the expression.
 */
export function normalizeStubProse(content: string): string {
  return content.replace(/\s+/g, ' ').trim().toLowerCase();
}

/**
 * The normalized prose every `stub-template-repeat` failure in `failures`
 * collapsed on, read back out of the same `sections` the failures were
 * computed from. A collapse group is keyed by identical content, so one
 * string per group is exact — not a summary.
 *
 * `sections` MUST be the extraction passed to `checkSectionDepth`; reading a
 * different serialization would compare prose the gate never saw.
 */
export function collapsedStubProse(
  sections: Array<{ id: string; content: string }>,
  failures: readonly SectionDepthFailure[],
): Set<string> {
  const proseById = new Map(sections.map((s) => [s.id, normalizeStubProse(s.content)]));
  const out = new Set<string>();
  for (const failure of failures) {
    for (const id of subsumedSectionIds(failure)) {
      const prose = proseById.get(id);
      if (prose) out.add(prose);
    }
  }
  return out;
}

/**
 * A collapsed stub finding names every section it subsumes inside its single
 * `sectionId`. The failure shape is persisted verbatim onto
 * `report.qaFindings.sectionDepth`, so it stays a plain string rather than
 * gaining a list field; the join and the parse therefore live together here so
 * they cannot drift. Section ids are field-name identifiers — never commas.
 */
const SUBSUMED_SECTION_ID_SEPARATOR = ', ';

/**
 * The sections a `stub-template-repeat` failure subsumes, as a list. Empty for
 * every other failure kind (their `sectionId` is a single section).
 */
function subsumedSectionIds(failure: SectionDepthFailure): string[] {
  if (failure.kind !== 'stub-template-repeat') return [];
  return failure.sectionId
    .split(SUBSUMED_SECTION_ID_SEPARATOR)
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * `failure.detail`, extended to name the sections a SECOND stub detector
 * observed carrying the same prose.
 *
 * The overlap skip in `runUserAudit` drops the auditor's Tier-2k twin of a
 * collapse, and that row names sections in the auditor's raw report-FIELD
 * namespace — keyFindings/risks/recommendations/insights/gridStudy, which
 * `extractSectionsFromReport` never yields. Dropping it silently leaves the
 * surviving row naming a strict SUBSET of the sections carrying the stub: on
 * a public-sentiment outage the operator reads 8 while 11 fields carry it.
 *
 * The two namespaces are not mappable in either direction (see
 * `normalizeStubProse`), so the lists are never merged into one count: a
 * section this gate calls `actionPlan` and the auditor calls
 * `developmentPathway` would be double-counted, and an id test that credits
 * candidate siblings instead deletes real ones. The co-detected list is
 * therefore appended WHOLE, in its own vocabulary, with its own total — an
 * enumeration that is internally consistent however the two engines spell a
 * section, at the cost of restating the names they agree on.
 *
 * It is appended only when the twin names a SECTION this gate did not name.
 * That test runs in the field-name identity of `stubSectionFieldAliases`, NOT
 * on the raw strings: the two engines spelling one section differently is the
 * NORM (`appendix`/`sentimentAppendix`, `actionPlan`/`developmentPathway` —
 * SECTION_FIELD_PATHS alone carries dozens of such divergences), so a
 * string-equality guard let alias divergence alone re-state sections the gate
 * had already named and inflate the apparent blast radius. A scan that found
 * exactly the same sections therefore adds nothing and is dropped, however it
 * spelled them.
 *
 * `coDetectedByProse` is keyed by `normalizeStubProse` output — the same
 * currency the skip matches on, and read HERE PER GROUP (`[failure]`, never a
 * union over the map): a report with two collapse groups must not name group
 * B's fields under group A's heading. `sections` MUST be the extraction the
 * failures were computed from, the same contract as `collapsedStubProse`;
 * `reportType` MUST be that report's type, since the alias resolution is
 * report-type-scoped.
 */
export function stubRepeatDetailWithCoDetectedFields(
  failure: SectionDepthFailure,
  sections: Array<{ id: string; content: string }>,
  coDetectedByProse: ReadonlyMap<string, readonly string[]>,
  reportType?: ReportTypeId,
): string {
  if (failure.kind !== 'stub-template-repeat') return failure.detail;
  if (coDetectedByProse.size === 0) return failure.detail;
  const coDetected = new Set<string>();
  // PER GROUP: only the prose THIS failure collapsed on. `collapsedStubProse`
  // is given `[failure]`, so a second collapse group's co-detected fields can
  // never leak into this group's clause.
  for (const prose of collapsedStubProse(sections, [failure])) {
    for (const id of coDetectedByProse.get(prose) ?? []) coDetected.add(id);
  }
  if (coDetected.size === 0) return failure.detail;
  const own = new Set<string>();
  for (const id of subsumedSectionIds(failure)) {
    for (const alias of stubSectionFieldAliases(reportType, id)) own.add(alias);
  }
  const ids = [...coDetected];
  if (ids.every((id) => own.has(id))) return failure.detail;
  return `${failure.detail} A field-level scan of the same report finds this text in`
    + ` ${ids.length} fields: ${ids.join(', ')}.`;
}

/**
 * Sections whose content a dedicated serializer ASSEMBLES from several report
 * fields instead of resolving through SECTION_FIELD_PATHS /
 * SECTION_FIELD_CANDIDATES. Every field the serializer reads is a spelling the
 * auditor may name the same section by. Keep in lockstep with the special
 * cases at the top of `serializeSectionContent`; `actionPlan`'s
 * executive-summary path (`conclusion.nextActions`) needs no entry because
 * `stubSectionFieldAliases` already credits a dotted path's root field.
 */
const COMPOSITE_SECTION_FIELDS: Record<string, readonly string[]> = {
  conclusion: ['conclusion', 'customBriefConclusion', 'btmDecisionSummary'],
  keyFindings: ['keyFindings', 'structuredFindings', 'projectIntentOverview'],
  criticalRisks: ['executiveSummaryCriticalRisks'],
  actionPlan: ['developmentPathway', 'recommendations', 'actionPlan'],
};

/**
 * The IDENTITY currency the two stub detectors' section NAMES are compared in
 * — the analogue of `normalizeStubProse`, which is the currency their CONTENT
 * is compared in.
 *
 * R-73 names canonical section ids; Tier-2k names raw report fields. This
 * resolves a gate section id to every field spelling it can be read from —
 * its report-type field path (and, for a dotted path, the top-level field the
 * auditor would flatten and name), its candidate chain, the fields a
 * composite serializer assembles it from, and the id itself.
 *
 * Only the FIRST of those routes is scoped by `reportType`; the candidate
 * chain, the composite fields and the id itself answer the same whatever is
 * passed. So a report whose collapse group reaches the auditor's top-level
 * object through some section's candidate chain — `gridCapacity`'s
 * ['gridCapacity', 'powerAnalysis'] is the live example — resolves that name
 * with or without the type, and cannot be used to pin the argument at the
 * `runUserAudit` call site. See the report-type test in `runUserAudit.test.ts`
 * for a group that can. (Stage 5.1 dropped `'gridStudy'` from the head of that
 * chain; the point the example makes is unchanged.)
 *
 * DIRECTION IS LOAD-BEARING. Only gate-id → field-names is total; the inverse
 * is not (`sentimentAppendix` is reachable from `appendix`, but a bare
 * `summary` belongs to no single id). So this answers exactly one question —
 * "did the twin name a section this gate did not?" — and is deliberately NOT
 * used to merge the two lists or their counts, and NOT used as the
 * suppression test in `runUserAudit`: content identity owns suppression,
 * because crediting candidate SIBLINGS there deleted real second stub groups
 * (the `rep-siblings` regression). Over-crediting here costs a clause, not a
 * finding.
 */
export function stubSectionFieldAliases(
  reportType: ReportTypeId | undefined,
  sectionId: string,
): Set<string> {
  const aliases = new Set<string>([sectionId]);
  const path = fieldPathForSection(reportType, sectionId);
  if (path) {
    aliases.add(path);
    // The auditor's extraction reads TOP-LEVEL report fields only and
    // flattens objects whole, so a dotted path is named there by its root.
    const root = path.split('.')[0];
    if (root) aliases.add(root);
  }
  for (const candidate of SECTION_FIELD_CANDIDATES[sectionId] ?? []) aliases.add(candidate);
  for (const field of COMPOSITE_SECTION_FIELDS[sectionId] ?? []) aliases.add(field);
  return aliases;
}

function describeShortfall(f: SectionDepthFailure): string {
  if (f.kind === 'below-min-bullets') {
    return `${f.observed.bulletCount} bullets, minimum ${f.threshold.bullets}`;
  }
  if (f.kind === 'bullets-too-short') {
    return `${f.observed.shortBullets} of ${f.observed.bulletCount} bullets under ${f.threshold.bulletChars} chars`;
  }
  return `${f.observed.chars} chars, minimum ${f.threshold.chars}`;
}

export function extractSectionsFromReport(report: SiteRagReport): Array<{ id: string; content: string }> {
  const reportType = report.reportType as ReportTypeId | undefined;
  const order =
    report.sectionOrder ??
    (reportType ? getSectionsForType(reportType).map((s) => s.sectionId) : []);

  const requiredIds = reportType ? (SECTION_DEPTH_REQUIREMENTS[reportType] ?? []).map((r) => r.sectionId) : [];
  const sectionIds = [...new Set([...order, ...requiredIds])];

  return sectionIds.map((id) => ({
    id,
    content: serializeSectionContent(report, id, reportType),
  }));
}

export function runSectionDepthGate(report: SiteRagReport): SectionDepthResult {
  const reportType = report.reportType as ReportTypeId;
  const sections = extractSectionsFromReport(report);
  // Read the omission stamp as plain data (an import of sectionOmissions here
  // would be circular — it imports extractSectionsFromReport from this file).
  const omitted = new Set((report.qaFindings?.sectionOmissions ?? []).map((o) => o.sectionId));
  const result = checkSectionDepth(sections, reportType, omitted, {
    degradedMode: report.degradedMode,
    narrativeFailureKind: report.generationTelemetry?.narrativeFailureKind,
  });

  if (result.failures.length > 0) {
    // Stamp onto the persisted artifact so the QA-1 quality hold
    // (resolveQualityHold) can act on block-severity failures and viewers can
    // render the findings — the callers discard this function's return value,
    // so the stamp is the only channel to the hold. Warn-severity entries are
    // persisted for observability but never hold.
    report.qaFindings = { ...(report.qaFindings ?? {}), sectionDepth: result.failures };
    console.warn('[R-73] Section depth gate failures:', {
      reportId: report.id,
      reportType,
      failures: result.failures,
    });
  } else if (report.qaFindings?.sectionDepth) {
    // A re-run (e.g. after precedence resolution deepened a section) must not
    // leave a stale hold-triggering stamp behind.
    delete report.qaFindings.sectionDepth;
  }

  const mode = readPublishSectionDepthGateMode();
  if (mode === 'block' && !result.passed) {
    const summary = result.failures.map((f) => f.detail).join(' ');
    throw new Error(`[R-73] Section depth gate blocked publish: ${summary}`);
  }

  return result;
}

/**
 * P1a (RCA 2026-08-03) — loop-time section-depth probe.
 *
 * The review loop runs BEFORE report assembly, so the full depth gate cannot
 * be evaluated there (e.g. `conclusion` is assembled after the loop). This
 * probe evaluates ONLY the requirements that are loop-visible — the executive
 * summary text and the Stage-1 findings that will become `structuredFindings`
 * — and returns the count of would-be block failures. Callers convert that
 * count into the `sectionDepthScore` component that was previously a
 * permanently-zero slot in the composite score.
 *
 * `findings` mirrors the serialized bullet rule: an entry counts when its
 * title + detail text meets `minBulletChars`.
 */
export function probeLoopVisibleSectionDepth(args: {
  reportType: ReportTypeId;
  executiveSummary?: string | null;
  findings?: ReadonlyArray<{ title?: string | null; detail?: string | null }> | null;
}): number {
  const requirements = (SECTION_DEPTH_REQUIREMENTS[args.reportType] ?? [])
    .filter((req) => !req.optional && (req.severity ?? 'block') === 'block')
    .filter((req) => req.sectionId === 'executiveSummary' || req.sectionId === 'keyFindings');
  let blockFailures = 0;
  for (const req of requirements) {
    if (req.sectionId === 'executiveSummary') {
      const min = req.minChars ?? DEFAULT_PROSE_MIN;
      const chars = (args.executiveSummary ?? '').trim().length;
      if (chars < min) blockFailures += 1;
      continue;
    }
    const minBullets = req.minBullets ?? 0;
    if (minBullets <= 0) continue;
    const minChars = req.minBulletChars ?? 0;
    const qualifying = (args.findings ?? []).filter((f) => {
      const text = [f?.title, f?.detail]
        .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
        .map((p) => p.trim())
        .join(' — ');
      return text.length >= minChars;
    }).length;
    if (qualifying < minBullets) blockFailures += 1;
  }
  return blockFailures;
}

export interface CrossSectionDuplicateParagraph {
  normalizedParagraph: string;
  sampleParagraph: string;
  sectionIds: string[];
  chars: number;
}

/**
 * Detect substantive paragraphs (>= 120 chars) that are repeated verbatim
 * across 2 or more non-alias sections (`STUB_ALIAS_FAMILY`), so generation
 * retry overlays and concision diagnostics can replace cross-section
 * copy-paste with concise cross-references.
 */
export function detectCrossSectionDuplicateParagraphs(
  sections: ReadonlyArray<{ id: string; content: string }>,
  omittedSections?: ReadonlySet<string>,
  minChars = 120,
): CrossSectionDuplicateParagraph[] {
  const groupable = sections.filter(
    (s) => !STUB_SKIP_SECTION_IDS.has(s.id) && !omittedSections?.has(s.id),
  );
  const byParagraph = new Map<
    string,
    { ids: Set<string>; sample: string; chars: number }
  >();
  for (const s of groupable) {
    const paragraphs = s.content
      .split(/\n{2,}|\r\n{2,}/)
      .map((p) => p.trim())
      .filter((p) => p.length >= minChars && !/^\s*(?:\d+\.|-|\*)\s+/.test(p));
    for (const para of paragraphs) {
      const norm = normalizeStubProse(para);
      if (norm.length < minChars) continue;
      const entry = byParagraph.get(norm) ?? {
        ids: new Set<string>(),
        sample: para,
        chars: para.length,
      };
      entry.ids.add(s.id);
      byParagraph.set(norm, entry);
    }
  }
  const out: CrossSectionDuplicateParagraph[] = [];
  for (const [normalizedParagraph, entry] of byParagraph.entries()) {
    if (entry.ids.size < 2) continue;
    const sectionIds = [...entry.ids].sort();
    const families = new Set(sectionIds.map((id) => STUB_ALIAS_FAMILY[id] ?? id));
    if (families.size < 2) continue;
    out.push({
      normalizedParagraph,
      sampleParagraph: entry.sample,
      sectionIds,
      chars: entry.chars,
    });
  }
  return out;
}

/**
 * P3b (RCA 2026-08-03) — generator-aimed retry overlay for block-severity
 * depth failures. Prepended to the narrative prompt on the single
 * budgeted regeneration (mirrors `buildCitationDensityRetryOverlay`), so
 * the retry attacks the failing sections at the source instead of burning
 * reviewer iterations that have no channel to repair depth.
 */
export function buildSectionDepthRetryOverlay(
  failures: readonly SectionDepthFailure[],
  duplicateParagraphs?: readonly CrossSectionDuplicateParagraph[],
): string {
  const lines = failures
    .filter((f) => f.severity === 'block')
    .slice(0, 6)
    .map((f) => `- ${f.detail}`);
  for (const dup of (duplicateParagraphs ?? []).slice(0, 3)) {
    lines.push(
      `- Cross-section duplicate paragraph (${dup.chars} chars) repeated across [${dup.sectionIds.join(', ')}]: keep full detail in the primary section only and cross-reference concisely in the others.`,
    );
  }
  if (lines.length === 0) return '';
  return [
    'SECTION DEPTH — PRIOR ATTEMPT FAILED THE DETERMINISTIC DEPTH GATE.',
    'The regenerated report MUST satisfy every minimum below; thin or missing sections are rejected:',
    ...lines,
    'Write substantive, site-specific content for each named section. Do not pad with boilerplate.',
    '',
  ].join('\n');
}

function extractBullets(content: string): string[] {
  const lines = content.split('\n');
  return lines
    .map((l) => l.match(/^\s*(?:\d+\.|-|\*)\s+(.+)$/)?.[1])
    .filter((b): b is string => !!b)
    .map((b) => b.trim());
}

/**
 * ALIAS FAMILIES (2026-08-13, Redhead PSR RCA) — ids that are DESIGNED
 * mirrors of one canonical narrative (the PSR's `executive` is deliberately
 * copied into `summary`/`executiveSummary` by siteRagService for list cards
 * and cross-type renderers). Identical prose WITHIN a family is
 * construction, not a stub. Lockstep twin of the auditor's
 * SECTION_ALIAS_FAMILY (siteReportAuditor checkTier2kStubTemplate).
 *
 * Families gate the (a) template-stub arm only. The (b) uniform-stub arm
 * counts FAILING sections instead: a mirror that is thin in all three copies
 * is one thin narrative, and naming it once is the honest report either way.
 */
const STUB_ALIAS_FAMILY: Record<string, string> = {
  executiveSummary: 'primary-narrative',
  summary: 'primary-narrative',
  executive: 'primary-narrative',
  appendix: 'appendix',
  sentimentAppendix: 'appendix',
};

function serializeSectionContent(
  report: SiteRagReport,
  sectionId: string,
  reportType?: ReportTypeId,
): string {
  if (sectionId === 'conclusion') {
    return serializeConclusion(report);
  }
  if (sectionId === 'keyFindings') {
    return serializeKeyFindings(report);
  }
  if (sectionId === 'criticalRisks') {
    return serializeCriticalRisks(report);
  }
  if (sectionId === 'actionPlan') {
    return serializeActionPlan(report, reportType);
  }
  if (isCustomBriefQuestionSectionId(sectionId)) {
    return serializeCustomBriefQuestion(report, sectionId);
  }

  const r = report as unknown as Record<string, unknown>;

  if (reportType) {
    const path = fieldPathForSection(reportType, sectionId);
    if (path) {
      const value = legacySectionFieldValue(path, getReportFieldValue(r, path));
      if (hasSectionFieldContent(value)) {
        return serializeValue(value);
      }
    }
  }

  const candidates = SECTION_FIELD_CANDIDATES[sectionId];
  if (candidates) {
    for (const field of candidates) {
      const value = legacySectionFieldValue(field, getReportFieldValue(r, field));
      if (hasSectionFieldContent(value)) {
        return serializeValue(value);
      }
    }
  }

  const customMap = r.sectionContent as Record<string, unknown> | undefined;
  if (customMap && hasSectionFieldContent(customMap[sectionId])) {
    return serializeValue(customMap[sectionId]);
  }

  return '';
}

/** Section text for depth, dedup, drift and user-audit gates reads only the
 * legacy keys of `publicSentimentMeta`: the additive psr-meta-v2 projection
 * (source excerpts, counts) is data for the redesigned view, not narrative,
 * and must not change any gate's outcome. */
function legacySectionFieldValue(path: string, value: unknown): unknown {
  return path === 'publicSentimentMeta' ? withoutPublicSentimentMetaV2(value) : value;
}

function serializeConclusion(report: SiteRagReport): string {
  const parts: string[] = [];
  const cbc = report.customBriefConclusion;
  if (cbc) {
    const legacyCbc = cbc as unknown as { summary?: string; actions?: Array<{ action?: string; rationale?: string }> };
    if (typeof legacyCbc.summary === 'string' && legacyCbc.summary.trim()) {
      parts.push(legacyCbc.summary);
    }
    parts.push(...(cbc.topStrengths ?? []));
    parts.push(...findingClaimEntriesToStrings(cbc.keyRisks));
    for (const a of cbc.recommendedNextActions ?? []) {
      if (a.action) parts.push(a.action);
      if (a.rationale) parts.push(a.rationale);
    }
    for (const a of legacyCbc.actions ?? []) {
      if (typeof a.action === 'string' && a.action.trim()) parts.push(a.action);
      if (typeof a.rationale === 'string' && a.rationale.trim()) parts.push(a.rationale);
    }
  }
  const conc = report.conclusion;
  if (conc) {
    if (conc.recommendation) parts.push(String(conc.recommendation));
    if (conc.btmDecisionSummary) parts.push(conc.btmDecisionSummary);
    parts.push(...(conc.topStrengths ?? []));
    parts.push(...(conc.topRisks ?? []));
    parts.push(...(conc.nextActions ?? []));
    parts.push(...(conc.keyConditions ?? []));
  }
  if (typeof report.btmDecisionSummary === 'string' && report.btmDecisionSummary.trim()) {
    parts.push(report.btmDecisionSummary);
  }
  // A conclusion list item the reviewer-voice gate WITHHELD is null on the
  // body (2026-09-18). It is not content: it neither counts toward depth nor
  // crashes the gate (this serializer runs unguarded at the persist boundary).
  return (parts as unknown[])
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .join('\n');
}

function serializeKeyFindings(report: SiteRagReport): string {
  const parts: string[] = [];
  // One `- ` bullet per finding so `extractBullets` counts REAL findings.
  // The pre-fix serializer emitted bare `title\ndetail\nimpact` lines, which
  // the `/^\s*(?:\d+\.|-|\*)\s+/` bullet matcher can never count — every
  // feasibility report scored 0/3 keyFindings bullets regardless of content
  // (the R-73 false-positive behind the 2026-08-03 Redhead RCA). Title and
  // detail are joined into a single bullet so `minBulletChars` measures the
  // finding's substance, not its shortest fragment; the impact enum word is
  // metadata, not prose, and is excluded from the measured text.
  for (const f of report.structuredFindings ?? []) {
    const text = [f.title, f.detail]
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
      .map((p) => p.trim())
      .join(' — ');
    if (text) parts.push(`- ${text}`);
  }
  const legacy = (report as unknown as Record<string, unknown>).keyFindings;
  if (Array.isArray(legacy)) {
    for (const item of legacy) {
      const text = serializeValue(item).trim();
      if (!text) continue;
      parts.push(text.startsWith('- ') ? text : `- ${text}`);
    }
  }
  // The intent overview is contextual prose, not a finding — appended
  // unbulleted so it can never inflate the counted finding count.
  if (report.projectIntentOverview) {
    parts.push(report.projectIntentOverview);
  }
  return parts.filter((p) => p.trim().length > 0).join('\n');
}

function serializeCriticalRisks(report: SiteRagReport): string {
  const risks = report.executiveSummaryCriticalRisks ?? [];
  return risks
    .map((r) => {
      const parts = [`- ${r.risk}`];
      if (r.mitigation) parts.push(r.mitigation);
      return parts.join(' ');
    })
    .join('\n');
}

function serializeActionPlan(report: SiteRagReport, reportType?: ReportTypeId): string {
  if (reportType === 'executive-summary') {
    const nextActions = report.conclusion?.nextActions ?? [];
    if (nextActions.length > 0) {
      return nextActions.map((a) => `- ${a}`).join('\n');
    }
  }
  const parts: string[] = [];
  const developmentPathway = (report as unknown as Record<string, unknown>).developmentPathway;
  if (typeof developmentPathway === 'string' && developmentPathway.trim()) {
    parts.push(developmentPathway);
  }
  for (const rec of report.recommendations ?? []) {
    if (rec.title) parts.push(`- ${rec.title}`);
    if (rec.description) parts.push(rec.description);
    if (rec.rationale) parts.push(rec.rationale);
  }
  const actionPlan = (report as unknown as Record<string, unknown>).actionPlan;
  if (Array.isArray(actionPlan)) {
    for (const item of actionPlan) {
      parts.push(typeof item === 'string' ? `- ${item}` : serializeValue(item));
    }
  }
  return parts.filter((p) => p.trim().length > 0).join('\n');
}

function serializeCustomBriefQuestion(report: SiteRagReport, sectionId: string): string {
  const questionId = parseCustomBriefQuestionSectionId(sectionId);
  if (!questionId) return '';
  const answer = report.customBriefAnswers?.find((a) => a.questionId === questionId);
  if (!answer) return '';
  const parts = [answer.findings, answer.evidence].filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  for (const flag of answer.redFlags ?? []) {
    const text = formatFindingClaimDisplay(flag);
    if (text.trim()) parts.push(text);
  }
  return parts.join('\n');
}

function serializeValue(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return `- ${item}`;
        if (item && typeof item === 'object') {
          const obj = item as Record<string, unknown>;
          if (typeof obj.title === 'string') return `- ${obj.title}`;
          if (typeof obj.risk === 'string') return `- ${obj.risk}`;
          if (typeof obj.text === 'string') return `- ${obj.text}`;
          if (typeof obj.detail === 'string') return `- ${obj.detail}`;
          if (typeof obj.claim === 'string') return `- ${obj.claim}`;
          if (typeof obj.findings === 'string') return obj.findings;
          return JSON.stringify(item);
        }
        return serializeValue(item);
      })
      .filter((s) => s.trim().length > 0)
      .join('\n');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj.summary === 'string') return obj.summary;
    if (typeof obj.text === 'string') return obj.text;
    if (typeof obj.narrative === 'string') return obj.narrative;
    const textFields = Object.values(obj).filter((v) => typeof v === 'string') as string[];
    if (textFields.length > 0) return textFields.join('\n');
    return JSON.stringify(value);
  }
  return String(value);
}

function readEnvFlag(name: string): string | undefined {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: unknown }).env != null) {
      const v = ((import.meta as { env: Record<string, unknown> }).env)[name];
      if (typeof v === 'string') return v;
    }
  } catch { /* import.meta unavailable in older Node */ }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}
